import { type AnchorHTMLAttributes, type HTMLAttributes, type ReactNode, forwardRef } from 'react';

import { cn } from '../utils/cn';

export interface SidebarProps extends HTMLAttributes<HTMLElement> {
  /**
   * Whether the sidebar is collapsible
   * @default false
   */
  collapsible?: boolean;

  /**
   * Whether the sidebar is collapsed
   * @default false
   */
  collapsed?: boolean;

  /**
   * Width of the sidebar when expanded
   * @default '256px'
   */
  width?: string;

  /**
   * Sidebar position
   * @default 'left'
   */
  position?: 'left' | 'right';
}

/**
 * Sidebar component
 *
 * A vertical navigation sidebar for application layouts.
 * Supports collapsible state and customizable width.
 *
 * @example
 * ```tsx
 * <Sidebar>
 *   <SidebarHeader>
 *     <h2>MedSuite</h2>
 *   </SidebarHeader>
 *   <SidebarContent>
 *     <nav>Navigation items...</nav>
 *   </SidebarContent>
 * </Sidebar>
 * ```
 */
export const Sidebar = forwardRef<HTMLElement, SidebarProps>(
  (
    {
      collapsible = false,
      collapsed = false,
      width = '256px',
      position = 'left',
      className,
      children,
      style,
      ...props
    },
    ref
  ) => {
    const baseStyles = ['flex flex-col', 'bg-white border-gray-200', 'transition-all duration-300'];

    const positionStyles = {
      left: 'border-r',
      right: 'border-l',
    };

    const finalWidth = collapsed && collapsible ? '64px' : width;

    return (
      <aside
        ref={ref}
        className={cn(baseStyles, positionStyles[position], className)}
        style={{ width: finalWidth, ...style }}
        {...props}
      >
        {children}
      </aside>
    );
  }
);

Sidebar.displayName = 'Sidebar';

export interface SidebarHeaderProps extends HTMLAttributes<HTMLDivElement> {}

/**
 * SidebarHeader component
 *
 * Header section of the sidebar, typically contains logo or title.
 */
export const SidebarHeader = forwardRef<HTMLDivElement, SidebarHeaderProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('p-4 border-b border-gray-200', className)} {...props}>
        {children}
      </div>
    );
  }
);

SidebarHeader.displayName = 'SidebarHeader';

export interface SidebarContentProps extends HTMLAttributes<HTMLDivElement> {}

/**
 * SidebarContent component
 *
 * Main content area of the sidebar, typically contains navigation.
 */
export const SidebarContent = forwardRef<HTMLDivElement, SidebarContentProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('flex-1 overflow-y-auto p-4', className)} {...props}>
        {children}
      </div>
    );
  }
);

SidebarContent.displayName = 'SidebarContent';

export interface SidebarFooterProps extends HTMLAttributes<HTMLDivElement> {}

/**
 * SidebarFooter component
 *
 * Footer section of the sidebar.
 */
export const SidebarFooter = forwardRef<HTMLDivElement, SidebarFooterProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('p-4 border-t border-gray-200', className)} {...props}>
        {children}
      </div>
    );
  }
);

SidebarFooter.displayName = 'SidebarFooter';

export interface SidebarItemProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  /**
   * Whether the item is active
   * @default false
   */
  active?: boolean;

  /**
   * Icon to display before text
   */
  icon?: ReactNode;
}

/**
 * SidebarItem component
 *
 * Individual navigation item for the sidebar.
 */
export const SidebarItem = forwardRef<HTMLAnchorElement, SidebarItemProps>(
  ({ active = false, icon, className, children, ...props }, ref) => {
    const baseStyles = [
      'flex items-center gap-3 px-3 py-2 rounded-lg',
      'transition-colors duration-200',
      'text-sm font-medium',
    ];

    const stateStyles = active
      ? 'bg-primary-50 text-primary-700'
      : 'text-gray-700 hover:bg-gray-100';

    return (
      <a ref={ref} className={cn(baseStyles, stateStyles, className)} {...props}>
        {icon && <span className="flex-shrink-0">{icon}</span>}
        <span>{children}</span>
      </a>
    );
  }
);

SidebarItem.displayName = 'SidebarItem';
